import Button from '@atoms/Button/Button';
import Cta from '@atoms/Cta/Cta';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { IContentCard } from '@models/IContentCard';
import { ILink } from '@models/ILink';
import SiteEnum from '@models/enums/SiteEnum';
import VariantTypeEnum from '@models/enums/VariantTypeEnum';
import backgroundColours, {
  darkBackgrounds,
} from '@utility/config/backgroundColours';
import { brightnessByColour } from '@utility/helpers/brightnessByColour';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

interface Props extends IContentCard {
  readonly backgroundColour?: string;
  readonly colourHexCode?: string;
  readonly hideBackground: boolean;
  readonly hideCta: boolean;
  readonly hideImage: boolean;
  readonly hideText: boolean;
  readonly isRichTextBody?: boolean;
  readonly site: SiteEnum;
  readonly truncateText?: boolean;
  readonly variant: Exclude<VariantTypeEnum, VariantTypeEnum.Cta>;
}

const ContentCard = ({
  backgroundColour,
  ctaStyle = VariantTypeEnum.Primary,
  ctas,
  hideBackground = false,
  hideCta,
  hideImage,
  hideText,
  image,
  isRichTextBody = false,
  site,
  text,
  title,
  truncateText = false,
  variant = VariantTypeEnum.Primary,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  const isDecoVariant = variant === VariantTypeEnum.Deco;

  const isDark =
    backgroundColour &&
    (isDecoVariant
      ? brightnessByColour(backgroundColour) < 127
      : darkBackgrounds.includes(backgroundColour));

  const backgroundColourHexCode =
    backgroundColour &&
    (isDecoVariant
      ? backgroundColour
      : backgroundColours?.[backgroundColour] || backgroundColour);

  useEffect(() => {
    if (ref.current) {
      const { current } = ref;
      setWidth(current.clientWidth);

      const handleResize = () => {
        setWidth(current.clientWidth);
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const CardText = ({
    plainText,
    richText,
  }: {
    plainText?: string;
    richText?: string;
  }) => (
    <Text
      variant={variant === VariantTypeEnum.Primary ? 'body' : 'body-small'}
      className={classNames({
        'tw-text-grey-80':
          !backgroundColourHexCode ||
          (variant === VariantTypeEnum.Secondary && !hideImage) ||
          (variant === VariantTypeEnum.Deco && image),
        'tw-line-clamp-5': truncateText,
      })}
      dangerouslySetInnerHTML={richText ? { __html: richText } : undefined}
    >
      {plainText}
    </Text>
  );

  const primaryVariantButton = ({ linkIcon, text, url, target }: ILink) => {
    switch (ctaStyle) {
      case VariantTypeEnum.Cta:
        return (
          <Cta
            site={site}
            linkIcon={linkIcon}
            text={text}
            url={url}
            target={target}
          />
        );
      case VariantTypeEnum.Secondary:
        return (
          <Button
            site={site}
            modifier="outline"
            as="link"
            key={text}
            href={url}
            target={target}
            label={text}
          />
        );
      default:
        return (
          <Button
            site={site}
            as="link"
            key={text}
            href={url}
            target={target}
            label={text}
          />
        );
    }
  };

  return (
    <article
      className={classNames(
        { 'tw-dark': isDark && !hideBackground },
        'tw-flex tw-h-full tw-flex-col'
      )}
    >
      {image && !hideImage && (
        <Image
          imageClasses="tw-w-full tw-object-cover"
          aspectRatio={isDecoVariant ? 1.25 : 1.6}
          sizes={`${width}px`}
          image={image}
        />
      )}
      <div
        ref={ref}
        className={classNames(
          {
            'tw-bg-grey-10': !backgroundColourHexCode && !hideBackground,
          },
          { 'tw-px-xs md:tw-px-sm xl:tw-p-md': !hideBackground },
          { 'tw-text-grey-100': backgroundColour && !isDark },
          { '!tw-px-sm !tw-py-lg': isDecoVariant && !image },
          {
            '!tw-text-white':
              (backgroundColour && isDecoVariant && !image && isDark) ||
              (backgroundColour && !hideBackground && isDark),
          },
          { '!tw-pl-0': !backgroundColour },
          'tw-flex tw-grow tw-flex-col tw-items-start tw-gap-xs tw-py-xs tw-text-grey-100'
        )}
        style={{
          backgroundColor:
            (isDecoVariant && !image) ||
            (backgroundColourHexCode && !hideBackground)
              ? backgroundColourHexCode
              : '',
        }}
      >
        <Text
          as="p"
          variant={
            variant === VariantTypeEnum.Primary
              ? 'headline'
              : 'heading-24-light'
          }
        >
          {title}
        </Text>
        {!hideText && (
          <>
            {isRichTextBody ? (
              <CardText richText={text} />
            ) : (
              <CardText plainText={text} />
            )}
          </>
        )}
        {!hideCta && !!ctas?.length && (
          <ul
            className={classNames(
              { 'tw-mt-auto': !isDecoVariant || image },
              'tw-grid tw-gap-4'
            )}
          >
            {ctas.map((cta: ILink) => (
              <li key={cta.url} className="tw-flex">
                {variant === VariantTypeEnum.Primary ? (
                  primaryVariantButton(cta)
                ) : (
                  <a
                    href={cta.url}
                    target={cta.target}
                    className="tw-font-sans tw-text-sm tw-underline tw-underline-offset-2"
                  >
                    {cta.text}
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
};

export default ContentCard;
